/**
 * The deck sheet: the day's water, who is standing on it, and who is in it.
 *
 * This screen is NOT drawn as a grid, and that is the whole design
 * argument. A court sheet is a grid because a court is one thing at a
 * time and the question is "what have you got at six" — a question about
 * a row. A pool is several things at once in the same water, and the
 * questions the desk is actually asked are different:
 *
 *   is the pool open, and if not why
 *   is anybody watching it
 *   how many are in it
 *   did that guest get billed
 *
 * None of those is a cell. So the day runs down the page as a list of
 * blocks with their coverage on them, and the deck — the list of people
 * — sits beside it, because those two are read together every time.
 *
 * ---------------------------------------------------------------------
 * THE COVERAGE STRIP IS THE FIRST THING ON THE SCREEN
 *
 * A pool with nobody on the stand must not be open. So the state of the
 * water is the headline, above the sheet and above the deck, and it says
 * 'uncovered' only when NOBODY is watching — a block that is one guard
 * short is short, which is a different emergency and reads differently.
 *
 * ---------------------------------------------------------------------
 * THE GATE IS ONE FORM
 *
 * The waiver, the sponsor and the fee are one server call, so they are
 * one form here. Splitting them across screens is what leaves a guest in
 * the water with an unbilled fee, and it is why "Guests unbilled" is on
 * the tiles rather than buried — that number is how a club discovers a
 * summer it never charged for.
 *
 * Every refusal is shown as written. "POOL-9: Rosalind Ashford cannot go
 * in the water — the pool waiver is superseded" is the whole answer, and
 * a paraphrase throws away the part the person at the desk has to say
 * out loud to the member in front of them.
 */

import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { Card, Empty, ErrorBanner, Loading, Status, Tile } from '../components/ui.tsx';
import { PersonLookup, type PersonHit } from '../components/Lookup.tsx';
import { clubToday, dayLabel, shiftDate } from '../lib/sheet.ts';
import { money } from '../lib/format.ts';

/* ------------------------------------------------------------------ */
/* Shapes                                                              */
/* ------------------------------------------------------------------ */

interface PoolArea {
  id: string;
  facility_id: string;
  code: string;
  name: string;
  kind: string;
  is_reservable: boolean;
  capacity: number | null;
}

interface Pool {
  id: string;
  code: string;
  name: string;
  kind: string;
  bather_load: number | null;
  lane_count: number;
  is_open: boolean | null;
  closed_because: string | null;
  areas: PoolArea[];
}

interface Session {
  session_id: string;
  facility_id: string;
  facility_name: string;
  swim_date: string;
  starts_at: string;
  ends_at: string;
  purpose: string;
  status: string;
  cancelled_reason: string | null;
  guards_required: number;
  guards_covering: number;
  guards_short: number;
  coverage: string;
  lanes_held: number;
  note: string | null;
}

interface Lane {
  reservation_id: string;
  starts_at: string;
  ends_at: string;
  status: string;
  area_name: string;
  session_purpose: string;
  membership_number: string | null;
  swimmer_name: string | null;
}

interface PoolEvent {
  event_id: string;
  name: string;
  kind: string;
  status: string;
  starts_at: string;
  ends_at: string;
  facility_id: string;
  takes_whole_pool: boolean;
  closes_the_pool: boolean;
  areas: string[];
  host: string | null;
}

interface DiaryEntry {
  entry_kind: string;
  entry_id: string;
  starts_at: string;
  ends_at: string;
  facility_id: string;
  instructor_name: string | null;
  what: string;
  status: string;
}

interface Sheet {
  date: string;
  sessions: Session[];
  lanes: Lane[];
  events: PoolEvent[];
  diary: DiaryEntry[];
}

interface Visit {
  visit_id: string;
  facility_id: string;
  facility_name: string;
  visitor_kind: string;
  person_id: string | null;
  swimmer_name: string | null;
  membership_number: string | null;
  sponsor_name: string | null;
  checked_in_at: string;
  checked_out_at: string | null;
  is_on_deck: boolean;
  was_charged: boolean;
  waiver_status: string;
  notes: string | null;
}

interface Count {
  facility_id: string;
  facility_name: string;
  bather_load: number | null;
  on_deck: string;
  visits_today: string;
  guests_today: string;
  guests_unbilled: string;
}

/* ------------------------------------------------------------------ */
/* Small shared bits                                                   */
/* ------------------------------------------------------------------ */

/** Postgres hands back 09:00:00; a sheet says 09:00. */
const hhmm = (t: string | null) => (t ? t.slice(0, 5) : '');


/**
 * Anchored at noon UTC on purpose: adding a day to a midnight date rolls
 * backwards across a daylight saving boundary, and a sheet that skips a
 * Sunday every spring is a bug nobody reproduces on a Tuesday.
 */
const clock = (iso: string | null) =>
  iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

/** What a block is for, said the way a club says it. */
const PURPOSE_LABEL: Record<string, string> = {
  open_swim: 'Open swim',
  lap: 'Lap swim',
  adult: 'Adult swim',
  team: 'Swim team',
  lesson: 'Swim lessons',
  private: 'Private hire',
};

/**
 * Coverage, as a tone and a phrase.
 *
 * 'uncovered' is critical and nothing else is, because it is the only
 * one that means nobody is watching the water. A block one guard short
 * is serious and is not the same thing, and painting them the same
 * colour is how a screen stops being read by August.
 */
function coverageStatus(s: Session) {
  switch (s.coverage) {
    case 'covered':
      return <Status tone="good">{s.guards_covering} on the stand</Status>;
    case 'partly_covered':
      return (
        <Status tone="serious">
          {s.guards_short} short of {s.guards_required}
        </Status>
      );
    case 'uncovered':
      return <Status tone="critical">Nobody watching</Status>;
    case 'not_required':
      return <Status tone="neutral">No guard needed</Status>;
    default:
      return <Status tone="neutral">Not running</Status>;
  }
}

/** The waiver, as the gate reads it. */
function waiverStatus(status: string) {
  switch (status) {
    case 'covered':
      return <Status tone="good">Signed</Status>;
    case 'expired':
      return <Status tone="serious">Expired</Status>;
    case 'superseded':
      return <Status tone="serious">On the old text</Status>;
    case 'unsigned':
      return <Status tone="critical">Unsigned</Status>;
    default:
      // A walk-up guest who is not a person record has nothing to hang a
      // signature from. Saying "unrecorded" is honest; saying "signed"
      // would be a false clean bill on exactly the person a club's
      // waiver policy is most about.
      return <Status tone="neutral">Not recorded</Status>;
  }
}

/* ------------------------------------------------------------------ */
/* The page                                                            */
/* ------------------------------------------------------------------ */

export function DeckSheetPage() {
  const { currency, club } = useSession();
  const zone = club?.timezone;
  // The club's today, not the browser's and not UTC's. It was
  // `new Date().toISOString().slice(0, 10)`, which for a club west of
  // Greenwich is tomorrow's empty sheet from early evening — the gate
  // closing up at seven concludes the day's visits have been lost.
  const [date, setDate] = useState(() => clubToday(zone));
  const [poolId, setPoolId] = useState<string | null>(null);

  const pools = useApi<{ pools: Pool[] }>('/v1/pool/pools');
  const list = pools.data?.pools ?? [];
  const selected = poolId ?? list[0]?.id ?? null;

  const sheet = useApi<Sheet>(
    selected ? `/v1/pool/sheet?date=${date}&facility_id=${selected}` : null,
    [date, selected],
  );
  const deck = useApi<{ visits: Visit[]; counts: Count[] }>(
    `/v1/pool/deck?date=${date}`,
    [date],
  );

  const pool = list.find((p) => p.id === selected) ?? null;
  const count = deck.data?.counts.find((c) => c.facility_id === selected) ?? null;

  const reloadAll = () => {
    sheet.reload();
    deck.reload();
    pools.reload();
  };

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        <div>
          <h1>The Deck</h1>
          <div className="muted small">
            <strong>{dayLabel(date, zone)}</strong> · who is watching the water, and who is in it
          </div>
        </div>
        <div className="spacer" />
        <div className="actions" style={{ flexWrap: 'nowrap' }}>
          <button className="secondary small" onClick={() => setDate(shiftDate(date, -1))}>
            ‹
          </button>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            aria-label="Date"
          />
          <button className="secondary small" onClick={() => setDate(shiftDate(date, 1))}>
            ›
          </button>
          {date !== clubToday(zone) && (
            <button className="secondary small" onClick={() => setDate(clubToday(zone))}>
              Today
            </button>
          )}
        </div>
      </div>

      <ErrorBanner error={pools.error ?? sheet.error ?? deck.error} />

      {list.length > 1 && (
        <div className="actions" style={{ marginBottom: 14 }}>
          {list.map((p) => (
            <button
              key={p.id}
              className={p.id === selected ? 'gold small' : 'secondary small'}
              onClick={() => setPoolId(p.id)}
            >
              {p.name}
            </button>
          ))}
        </div>
      )}

      {/* ------------------------------------------------------------ */}
      {/* Is the pool open, and is anybody watching it. Above           */}
      {/* everything, because it is the question every other question   */}
      {/* on this screen depends on.                                    */}
      {/* ------------------------------------------------------------ */}
      {pool && pool.is_open === false && (
        <div className="error-banner">
          <strong>{pool.name} is closed.</strong> {pool.closed_because}
        </div>
      )}

      <div className="tiles">
        <Tile
          label="On the deck"
          value={count ? count.on_deck : '—'}
          note={
            pool?.bather_load
              ? `posted load ${pool.bather_load}`
              : 'no posted load'
          }
        />
        <Tile label="Visits today" value={count ? count.visits_today : '—'} />
        <Tile label="Guests" value={count ? count.guests_today : '—'} />
        <Tile
          label="Guests unbilled"
          value={count ? count.guests_unbilled : '—'}
          tone={count && Number(count.guests_unbilled) > 0 ? 'serious' : 'good'}
          note={
            count && Number(count.guests_unbilled) > 0
              ? 'signed in, never charged'
              : 'every guest charged'
          }
        />
      </div>

      <div className="grid-2">
        <TheDay
          date={date}
          sheet={sheet.data}
          loading={sheet.loading}
          onChanged={reloadAll}
        />
        <TheGate selected={selected} date={date} onChanged={reloadAll} />
      </div>

      <WhoIsHere
        visits={deck.data?.visits ?? []}
        loading={deck.loading}
        currency={currency}
        onChanged={reloadAll}
      />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* The day                                                             */
/* ------------------------------------------------------------------ */

function TheDay({
  date,
  sheet,
  loading,
  onChanged,
}: {
  date: string;
  sheet: Sheet | null;
  loading: boolean;
  onChanged: () => void;
}) {
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const cancel = useMutation(async (id: string, why: string) => {
    const r = await api<{ cancelled_lanes: { swimmer_name: string | null }[] }>(
      `/v1/pool/sessions/${id}/cancel`,
      { method: 'POST', body: { reason: why } },
    );
    setCancelling(null);
    setReason('');
    onChanged();
    return r;
  });

  if (loading) return <Loading rows={5} />;
  if (!sheet || sheet.sessions.length === 0) {
    return (
      <Card title="The Day">
        <Empty>
          Nothing is open on this day. Either the sheet has not been opened yet,
          or the date is outside the season — which is not the same as the pool
          being closed, and is why nothing here says so.
        </Empty>
      </Card>
    );
  }

  return (
    <>
      <ErrorBanner error={cancel.error} />
      <Card title="The Day" flush>
        <table className="data">
          <thead>
            <tr>
              <th>Hours</th>
              <th>What for</th>
              <th>Guards</th>
              <th>Lanes held</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sheet.sessions.map((s) => (
              <tr
                key={s.session_id}
                className={s.status === 'cancelled' ? 'muted' : undefined}
              >
                <td className="nowrap">
                  {hhmm(s.starts_at)}–{hhmm(s.ends_at)}
                </td>
                <td>
                  {PURPOSE_LABEL[s.purpose] ?? s.purpose}
                  {s.status === 'cancelled' && (
                    <div className="hint">Cancelled — {s.cancelled_reason}</div>
                  )}
                  {s.note && <div className="hint">{s.note}</div>}
                </td>
                <td>{coverageStatus(s)}</td>
                <td>{s.lanes_held || '—'}</td>
                <td className="num">
                  {s.status === 'open' && cancelling !== s.session_id && (
                    <button className="secondary small" onClick={() => setCancelling(s.session_id)}>
                      Cancel
                    </button>
                  )}
                  {cancelling === s.session_id && (
                    <div className="actions">
                      <input
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Why — this reaches the members holding lanes"
                        autoFocus
                      />
                      <button
                        className="gold"
                        disabled={cancel.busy || reason.trim().length < 3}
                        onClick={() => cancel.run(s.session_id, reason)}
                      >
                        Cancel it
                      </button>
                      <button className="secondary small" onClick={() => setCancelling(null)}>
                        Keep it
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {sheet.lanes.length > 0 && (
        <Card title="Lanes Held" flush>
          <table className="data">
            <thead>
              <tr>
                <th>Time</th>
                <th>Lane</th>
                <th>Who</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {sheet.lanes.map((l) => (
                <tr key={l.reservation_id} className={l.status === 'cancelled' ? 'muted' : undefined}>
                  <td className="nowrap">
                    {hhmm(l.starts_at)}–{hhmm(l.ends_at)}
                  </td>
                  <td>{l.area_name}</td>
                  <td>
                    {l.swimmer_name ?? l.membership_number ?? '—'}
                    {l.membership_number && l.swimmer_name && (
                      <span className="hint"> {l.membership_number}</span>
                    )}
                  </td>
                  <td>{l.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {sheet.diary.length > 0 && (
        <Card title="Lessons and Classes" flush>
          <table className="data">
            <thead>
              <tr>
                <th>Time</th>
                <th>What</th>
                <th>Who is teaching</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {sheet.diary.map((d) => (
                <tr key={d.entry_id} className={d.status === 'cancelled' ? 'muted' : undefined}>
                  <td className="nowrap">
                    {hhmm(d.starts_at)}–{hhmm(d.ends_at)}
                  </td>
                  <td>{d.what}</td>
                  <td>{d.instructor_name ?? '—'}</td>
                  <td>{d.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {sheet.events.length > 0 && (
        <Card title="What Else Has the Water" flush>
          <table className="data">
            <thead>
              <tr>
                <th>Time</th>
                <th>Event</th>
                <th>Takes</th>
                <th>Host</th>
              </tr>
            </thead>
            <tbody>
              {sheet.events.map((e) => (
                <tr key={e.event_id}>
                  <td className="nowrap">
                    {hhmm(e.starts_at)}–{hhmm(e.ends_at)}
                  </td>
                  <td>
                    {e.name}
                    {e.closes_the_pool && (
                      <div className="hint">The pool is closed to members for this.</div>
                    )}
                  </td>
                  <td>
                    {e.takes_whole_pool ? 'The whole pool' : e.areas.join(', ') || '—'}
                  </td>
                  <td>{e.host ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* The gate                                                            */
/* ------------------------------------------------------------------ */

/**
 * One form, because the server does it as one operation.
 *
 * The "sign it here" checkbox is the important control on this screen.
 * Without it, an unsigned member on a Saturday means somebody goes home
 * to find an email — which is the failure the whole gate exists to
 * prevent, and it is worth a checkbox being the most obvious thing after
 * the name.
 */
function TheGate({
  selected,
  date,
  onChanged,
}: {
  selected: string | null;
  date: string;
  onChanged: () => void;
}) {
  const { currency } = useSession();
  const [kind, setKind] = useState('member');
  // The person, as chosen. This was a text box asking for a uuid, which
  // no screen in this product shows anybody — so the gate could sign in
  // walk-ups by name and nobody the club actually knows.
  const [person, setPerson] = useState<PersonHit | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [sponsor, setSponsor] = useState<PersonHit | null>(null);
  const [age, setAge] = useState('');
  const [signWaiver, setSignWaiver] = useState(false);
  const [last, setLast] = useState<{ name: string; charge: number | null } | null>(null);

  const isGuest = kind === 'member_guest';
  const isStaff = kind === 'staff' || kind === 'contractor';

  const checkIn = useMutation(async () => {
    const r = await api<{
      visit: { swimmer_name: string | null };
      charge: { extended_minor: string; tax_minor: string } | null;
    }>('/v1/pool/check-in', {
      method: 'POST',
      body: {
        facility_id: selected,
        visitor_kind: kind,
        person_id: person?.person_id ?? null,
        display_name: displayName.trim() || null,
        guest_age: age.trim() ? Number(age) : null,
        sponsor_person_id: sponsor?.person_id ?? null,
        swim_date: date,
        sign_waiver: signWaiver,
        signed_by_person_id: signWaiver ? (person?.person_id ?? null) : null,
      },
    });
    setLast({
      name: r.visit.swimmer_name ?? displayName,
      charge: r.charge ? Number(r.charge.extended_minor) + Number(r.charge.tax_minor) : null,
    });
    setPerson(null);
    setDisplayName('');
    setSponsor(null);
    setAge('');
    setSignWaiver(false);
    onChanged();
    return r;
  });

  const clear = useMutation(async () => {
    await api('/v1/pool/deck/clear', { method: 'POST', body: {} });
    onChanged();
  });

  return (
    <Card
      title="Sign Somebody In"
      actions={
        <button className="secondary small" disabled={clear.busy} onClick={() => clear.run()}>
          Clear the board
        </button>
      }
    >
      <ErrorBanner error={checkIn.error ?? clear.error} />

      {last && !checkIn.error && (
        <div className="ok-banner">
          <strong>{last.name}</strong> is on the deck.{' '}
          {last.charge !== null
            ? `Guest fee ${money(last.charge, currency)} on the member's account.`
            : 'No fee.'}
        </div>
      )}

      <label className="field">
        <span>Who is this</span>
        <select value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="member">A member</option>
          <option value="dependent">A member's child</option>
          <option value="member_guest">A member's guest</option>
          <option value="staff">Staff</option>
          <option value="contractor">The pool company</option>
        </select>
      </label>

      <PersonLookup
        label="Somebody the club knows"
        hint={
          isStaff
            ? 'Staff need no member waiver — their paperwork is an employment matter, and being at the water at all is a certification.'
            : 'A person record is what a waiver signature hangs from.'
        }
        value={person}
        onChange={setPerson}
        // Guards, the pool company and off-season staff are all people
        // the gate has to be able to sign in, and half of them are not on
        // a live membership.
        includeInactive={isStaff}
      />

      <label className="field">
        <span>…or a name</span>
        <input
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="A walk-up guest"
        />
      </label>
      <div className="hint">
        A guest who swims once is a name; one who swims every Saturday ends up a
        person record. The gate should not force that choice with a queue behind
        them.
      </div>

      {isGuest && (
        <>
          <PersonLookup
            label="Signed for by"
            hint="The account is worked out from them. A guest fee with nobody answerable is a write-off."
            value={sponsor}
            onChange={setSponsor}
            filter={(hit) => hit.may_charge !== false && hit.member_account_id !== null}
          />

          <label className="field">
            <span>Age</span>
            <input
              value={age}
              onChange={(e) => setAge(e.target.value)}
              inputMode="numeric"
              placeholder="Only if the club has a children's rate"
            />
          </label>
        </>
      )}

      {!isStaff && (
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 9,
            margin: '4px 0 14px',
            cursor: 'pointer',
          }}
        >
          {/* Not a `.field`: that class is display:block and every input
              in this app is width:100%, which stacks a checkbox above
              its own label and reads as two unrelated controls. */}
          <input
            type="checkbox"
            checked={signWaiver}
            onChange={(e) => setSignWaiver(e.target.checked)}
            style={{ width: 'auto', flex: '0 0 auto', marginTop: 2 }}
          />
          <span>
            <span style={{ fontWeight: 600, fontSize: '12.5px' }}>
              They are signing the waiver here, now
            </span>
            <span className="hint" style={{ display: 'block', marginTop: 2 }}>
              On the tablet, at the gate. Sending somebody home to check their
              email on a Saturday is the failure this exists to avoid.
            </span>
          </span>
        </label>
      )}

      <button
        className="gold"
        disabled={checkIn.busy || !selected || (!person && !displayName.trim())}
        onClick={() => checkIn.run()}
      >
        {checkIn.busy ? 'Signing in…' : 'Sign in'}
      </button>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Who is here                                                         */
/* ------------------------------------------------------------------ */

function WhoIsHere({
  visits,
  loading,
  currency,
  onChanged,
}: {
  visits: Visit[];
  loading: boolean;
  currency: Parameters<typeof money>[1];
  onChanged: () => void;
}) {
  const checkOut = useMutation(async (id: string) => {
    await api(`/v1/pool/visits/${id}/check-out`, { method: 'POST' });
    onChanged();
  });

  if (loading) return <Loading rows={4} />;

  return (
    <>
      <ErrorBanner error={checkOut.error} />
      <Card title="Who Is Here" flush>
        {visits.length === 0 ? (
          <Empty>Nobody has been signed in on this day.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>In</th>
                <th>Out</th>
                <th>Who</th>
                <th>As</th>
                <th>Signed for by</th>
                <th>Waiver</th>
                <th>Charged</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visits.map((v) => (
                <tr key={v.visit_id} className={v.is_on_deck ? undefined : 'muted'}>
                  <td className="nowrap">{clock(v.checked_in_at)}</td>
                  <td className="nowrap">{clock(v.checked_out_at) || '—'}</td>
                  <td>
                    {v.swimmer_name ?? '—'}
                    {v.membership_number && (
                      <span className="hint"> {v.membership_number}</span>
                    )}
                  </td>
                  <td>{v.visitor_kind.replace('_', ' ')}</td>
                  <td>{v.sponsor_name ?? '—'}</td>
                  <td>{waiverStatus(v.waiver_status)}</td>
                  <td>
                    {v.visitor_kind === 'member_guest' ? (
                      v.was_charged ? (
                        <Status tone="good">Yes</Status>
                      ) : (
                        <Status tone="serious">No</Status>
                      )
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="num">
                    {v.is_on_deck && (
                      <button
                        className="secondary small"
                        disabled={checkOut.busy}
                        onClick={() => checkOut.run(v.visit_id)}
                      >
                        Check out
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}
